import React from 'react'

/**
 * Enhanced lazy import utility with error handling
 * @param importFn - Function that returns a dynamic import promise
 * @param namedExport - Optional named export to extract (if not using default export)
 * @returns React lazy component
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyImport<T extends React.ComponentType<any>>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    importFn: () => Promise<any>,
    namedExport?: string,
): React.LazyExoticComponent<T> {
    return React.lazy(() =>
        importFn()
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .then((module: any) => {
                // If namedExport is specified, extract that export
                if (namedExport && namedExport in module) {
                    return { default: module[namedExport] }
                }
                // Otherwise, return the default export
                return 'default' in module ? module : { default: module }
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.error('Failed to load component:', error)
                // Return a fallback component
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const FallbackComponent: React.ComponentType<any> = () =>
                    React.createElement(
                        'div',
                        { style: { padding: '2rem', textAlign: 'center', color: '#ef4444' } },
                        React.createElement('h3', null, 'Failed to load component'),
                        React.createElement('p', null, 'Please refresh the page and try again.'),
                    )
                return { default: FallbackComponent }
            }),
    )
}

/**
 * Preload a lazy component for better performance
 * @param lazyComponent - React lazy component to preload
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function preloadComponent(lazyComponent: React.LazyExoticComponent<any>): void {
    // Access the internal _payload to trigger preloading
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const componentImporter = (lazyComponent as any)._payload
    if (componentImporter && typeof componentImporter === 'function') {
        componentImporter()
    }
}
